<script setup lang="ts">
import { computed, getCurrentInstance, ref } from "vue";

import { VueECharts as VChart } from "@Helpers/echarts";
import useTranslation from "@Composables/useTranslation";
import { useHandleResize, ResizableChart } from "@Composables/useHandleResize";
import { ValueChartDataProvider } from "@Modules/analytics/Components/PercentValueChart/ValueChart";
import ChartLayout from "@Modules/analytics/Components/ChartLayout.vue";

//
// Props
interface Props {
  dataset: ValueChartDataProvider;
  title?: string;
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  title: "Popular Times",
  loading: false,
})

//
// Setup
const { __ } = useTranslation()
const instance = getCurrentInstance()

//
// Data
const chart = ref<ResizableChart | null>(null)

useHandleResize(chart);

//
// Computed
const chartData = computed(() => props.dataset.getChartData())
const chartLabels = computed(() => props.dataset.getChartLabels())

const chartOptions = computed<unknown>(() => ({
  title: {
    text: props.title,
    show: false,
  },
  grid: {
    left: "14%",
    top: "4%",
    right: "2%",
    bottom: "10%",
  },
  tooltip: {
    trigger: "axis",
    axisPointer: {
      type: "none",
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
    formatter: (params: any) => {
      let content = params[0]?.data?.name || ""
      for (let i = 0; i < params.length; i++) {
        content +=
          "<br>" +
          params[i].marker +
          params[i].seriesName +
          ": " +
          (params[i]?.value || "").toLocaleString()
      }

      return content
    },
  },
  toolbox: {
    show: true,
    feature: {
      saveAsImage: { show: true, title: __("chart.save_as_image") },
    },
  },
  xAxis: [
    {
      type: "category",
      max: chartData.value.length - 1,
      axisLabel: {
        fontWeight: "bolder",
      },
      data: chartLabels.value,
      axisLine: {
        show: false,
      },
    },
  ],
  yAxis: [
    {
      type: "value",
      splitNumber: 3,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
  ],
  series: [
    {
      // @ts-ignore this is a valid way to get the internal component id
      id: `barchart_data_${instance._uid}`,
      type: "bar",
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 2,
          shadowColor: "rgba(0, 0, 0, 0.4)",
        },
      },
      name: __("chart.clicks"),
      data: chartData.value,
      itemStyle: {
        color: "#9bc225",
      },
    },
  ],
}))


</script>

<template>
  <ChartLayout
    class="vertical-bar"
    :is-loading="loading"
  >
    <template #header>
      {{ props.title }}
    </template>

    <v-chart
      ref="chart"
      class="vertical-bar__chart"
      :options="chartOptions"
    />
  </ChartLayout>
</template>

<style lang="scss" scoped>
.vertical-bar {
  &__chart {
    width: 100%;
    min-width: 0;
    height: 200px;
  }
}
</style>
